import Sentry from "@similarweb/sentry-logger";

import { ASSETS_ERROR, WEBSITE_ID_KEY } from "app/components/dashboard/widget-wizard/components/segments/constants";
import { DASHBOARD_ID_PREVIEW_MODE } from "app/components/dashboard/widget-wizard/constants";
import { selectCurrentDashboardId } from "app/components/dashboard/widget-wizard/selectors/dashboardSelectors";
import type { ISegmentData } from "app/pages/conversion/ConversionSegmentsUtils";
import { loadCustomSegmentsMetadataForDashboard } from "app/pages/segments/config/segmentsConfigHelpers";
import { granularityConfigs } from "app/pages/website-analysis/website-content/leading-folders/FolderAnalysisDefaults";
import SegmentsApiService from "app/services/segments/segmentsApiService";
import { SwRedux } from "app/services/SwRedux";
import { generateLineChartData } from "pro-features/components/Chart/src/components/PeriodOverPeriodChart/PoPChartDataGenerator";
import type { ISeries } from "pro-features/components/Chart/src/components/PeriodOverPeriodChart/PoPChartTypes";
import { granularities } from "pro-features/utils";

import markerWithDashedConfig from "pro-features/components/Chart/src/configs/series/markerWithDashedLinePerPointChartConfig";
import { getChartConfig } from "../../widget-utilities/segments/chartConfig";
import type { LegendPropsTypes } from "../../widget-utilities/segments/LegendItems";
import { LegendItems } from "../../widget-utilities/segments/LegendItems";
import type {
    GraphDataEntity,
    POPResponseEntity,
    POPSeries,
    SegmentsResponseEntity,
    TransformedDataTypes,
    SingleSegmentSeriesType,
} from "../../widget-utilities/segments/types";
import { GraphWidget } from "../GraphWidget";
import { WidgetState } from "../Widget";

import type { DashboardMetric } from "./type";

export class SegmentsMonthlyVisitsGraphWidget extends GraphWidget {
    protected metric: DashboardMetric;

    public currentSegmentsData = [];
    public hasWebsite: boolean;

    reactLegendComponent: ({ legendItems, colors }: LegendPropsTypes) => React.ReactNode;
    segmentApiService = new SegmentsApiService();

    constructor() {
        super();
        this.metric = "Visits";
    }

    public isPptSupported = () => {
        return false;
    };

    static getWidgetDashboardType() {
        return "Graph";
    }

    static getWidgetResourceType() {
        return "Graph";
    }

    static getWidgetMetadataType() {
        return "SegmentsMonthlyVisitsGraph";
    }

    public setCurrentSegments = (segments: ISegmentData[], id: string) => {
        if (segments) {
            segments.forEach((segment) => {
                if (segment.id === id) {
                    this.currentSegmentsData.push(segment);
                }
            });
        }
    };

    public getSegmentsMetaData = () => {
        const state = this._reduxService.getState();
        const { segmentId, keys } = this._params;

        const dashboardSegmentsMeta = state.segmentsModule?.dashboardSegmentsMeta;
        const customSegmentsMeta = state.segmentsModule.customSegmentsMeta;

        const segmentsMeta = customSegmentsMeta || dashboardSegmentsMeta;
        const id = keys || segmentId;

        if (segmentsMeta && !this.currentSegmentsData.length) {
            this.setCurrentSegments(segmentsMeta.Segments, id);

            if (this.currentSegmentsData.length === 0 && customSegmentsMeta) {
                this.setCurrentSegments(segmentsMeta.AccountSegments, id);
            }
        } else {
            this.widgetState = WidgetState.ERROR;
        }
    };

    public fetchSegmentData = async (): Promise<POPResponseEntity | SegmentsResponseEntity> => {
        const { segmentId, webSource } = this._params;

        const timeGranularity = this.durationObject?.forAPI?.isWindow ? granularities[0] : granularities[2];

        // @ts-ignore FIXME: strict migration - Type 'unknown' is not assignable to type 'SegmentsResponseEntity | POPResponseEntity'
        return this.segmentApiService.getCustomSegmentEngagementData({
            country: this._params.country,
            from: this.durationObject.forAPI.from,
            to: this.durationObject.forAPI.to,
            compareFrom: this.durationObject?.forAPI?.compareFrom,
            compareTo: this.durationObject?.forAPI?.compareTo,
            includeSubDomains: true,
            isWindow: this.durationObject?.forAPI?.isWindow,
            keys: null,
            segmentsIds: [segmentId],
            timeGranularity,
            webSource,
            lastUpdated: this.currentSegmentsData[0].lastUpdated,
        });
    };

    getFormattedChartData = (data: GraphDataEntity[]) =>
        data.map((item) => [
            new Date(item.Key).getTime(),
            item.Value.Value,
            {
                partial: item.Value.Confidence >= 0.3 && item.Value.Confidence < 1,
                hallowMarker: item.Value.Confidence >= 0.3 && item.Value.Confidence < 1,
                confidenceLevel: item.Value.Confidence,
            },
        ]);

    public calcChange = (currentMonth: number, comparedMonth: number): number =>
        (currentMonth - comparedMonth) / comparedMonth;

    public getFormattedPoPChartData = (data: GraphDataEntity[], compareData: GraphDataEntity[]): ISeries[] =>
        data.map((item, index) => ({
            Values: [
                {
                    Key: item.Key,
                    Value: item.Value.Value,
                    Confidence: item.Value.Confidence,
                },
                {
                    Key: compareData[index]?.Key,
                    Value: compareData[index]?.Value.Value,
                    Confidence: compareData[index]?.Value.Confidence,
                },
            ],
            Change: [this.calcChange(item.Value.Value, compareData[index]?.Value.Value)],
        }));

    public async getChartSeries(
        data: SegmentsResponseEntity | POPResponseEntity,
        compareItemsKeys?: string[],
    ): Promise<SingleSegmentSeriesType[] | POPSeries[]> {
        const isPOP = this.durationObject.forAPI.compareFrom;

        // Checking isPOP here allows us to override Period over Period chart logic

        if (isPOP) {
            return this.getPOPChartSeries(data as POPResponseEntity);
        }

        const { segmentId, timeGranularity } = this._params;
        const graphData = data[segmentId][this.metric][timeGranularity].Graph;

        const config = markerWithDashedConfig({
            data: this.getFormattedChartData(graphData),
            isDataSingleSeries: true,
        }) as SingleSegmentSeriesType;

        config["displayName"] = this.currentSegmentsData?.[0]?.domain;

        return [config];
    }

    public getPOPChartSeries(data: POPResponseEntity): POPSeries[] {
        const { segmentId, timeGranularity } = this._params;

        const comparedInterval = data.ComparedInterval[segmentId][this.metric][timeGranularity].Graph;
        const interval = data.Interval[segmentId][this.metric][timeGranularity].Graph;

        const comparePoPData: ISeries[] = this.getFormattedPoPChartData(interval, comparedInterval);

        return this.getPOPData(comparePoPData);
    }

    public getPOPData(data: ISeries[]): POPSeries[] {
        const { webSource } = this._params;

        const lineChartData = generateLineChartData([webSource], data) as POPSeries[];
        const durationTitles = this.durationObject?.forWidget;
        let durationTitlesForPOP = [];

        if (Array.isArray(durationTitles)) {
            durationTitlesForPOP = [...durationTitles].reverse();
        }

        lineChartData.forEach((data, index) => {
            data["displayName"] = durationTitlesForPOP.length ? durationTitlesForPOP?.[index] : durationTitles;
        });

        return lineChartData;
    }

    getGraphData(transformedData: TransformedDataTypes, metric?: DashboardMetric) {
        const type = "line";
        const { timeGranularity, segmentsGroupId } = this._params;
        const durationApi = this.durationObject;
        const format = "MMM";
        const currentGranularity = granularityConfigs[timeGranularity];
        const isSegmentsGroup = !!segmentsGroupId || !!this._widgetConfig.segmentsGroupId;
        const filterFormatName = this._metricTypeConfig?.y_axis?.format;

        return getChartConfig(
            transformedData,
            durationApi,
            timeGranularity,
            type,
            format,
            this.currentSegmentsData,
            currentGranularity,
            metric,
            isSegmentsGroup,
            filterFormatName,
        );
    }

    async initChart(data: POPResponseEntity | SegmentsResponseEntity) {
        if (data) {
            const isSegmentsGroup = this._params.segmentsGroupId || this._widgetConfig.segmentsGroupId;
            const isPOP = !!this.durationObject.forAPI.compareFrom;

            const formattedData: SingleSegmentSeriesType[] | POPSeries[] = await this.getChartSeries(data);

            const chartOptions = this.getChartOptions(formattedData);
            const graphConf = this.getGraphData(formattedData);

            graphConf.series = formattedData;

            this.chartConfig = {
                ...this.getHighChartsConfig(chartOptions, formattedData, []),
                ...graphConf,
            };

            this.chartConfig["export"] = {
                title: this.getViewData().title,
                csvUrl: this.excelUrl,
            };

            const chartConfig = this.chartConfig;

            this.chartConfig.exportPng = (chartTitle, utilityData) => {
                this._pngExportService.export(chartConfig, chartTitle, this._params, utilityData);
            };

            if (isPOP || isSegmentsGroup) {
                const legends = [];
                const segmentsData = this.currentSegmentsData[0];

                chartConfig.series.forEach(({ name, color, displayName, isWebsite }, index) => {
                    legends.push({
                        name,
                        color,
                        displayName: displayName || this.durationObject.forWidget[index],
                        isWebsite,
                        segmentName: segmentsData?.segmentName,
                        domain: segmentsData?.domain,
                        isPOP,
                    });
                });

                this.legendItems = legends;
            }

            this.reactLegendComponent = LegendItems;
            this.originalData = data;
        }
    }

    public async getData() {
        const state = this._reduxService.getState();

        let isEnabled = true;

        if (this.id) {
            const segmentWidgetAvailability = state.segmentsModule?.segmentWidgetAvailability?.[this.id];

            if (segmentWidgetAvailability) {
                isEnabled = segmentWidgetAvailability?.isEnabled;
            }
        }

        if (!isEnabled) {
            this.widgetState = WidgetState.SOFT_LIMIT_REACHED;

            return;
        }

        try {
            const { selectedSegmentsId } = this._params;

            this.hasWebsite = !!selectedSegmentsId?.find((id: string) => id.includes(WEBSITE_ID_KEY));

            this.getSegmentsMetaData();

            if (!this.currentSegmentsData.length) {
                const reduxStore = SwRedux.getInstance().getState();
                const storeDashboardId = selectCurrentDashboardId(reduxStore);
                const id = this.dashboardId === DASHBOARD_ID_PREVIEW_MODE ? storeDashboardId : this.dashboardId;

                await loadCustomSegmentsMetadataForDashboard(id);

                this.getSegmentsMetaData();
            }

            if (this.currentSegmentsData.length) {
                const data = await this.fetchSegmentData();

                if (data) {
                    await this.initChart(data);
                    this.widgetState = WidgetState.LOADED;

                    this.notifyWidgetUpdated();
                } else {
                    this.widgetState = WidgetState.EMPTY;
                }
            }
        } catch (error) {
            const errorName = error?.name;

            if (errorName) {
                try {
                    const parsedError = JSON.parse(errorName);
                    const errorCode = ASSETS_ERROR[parsedError?.ErrorCode];

                    if (errorCode) {
                        this.widgetState = WidgetState.ASSET_NOT_ACCESSIBLE;

                        this.errorConfig.messageTitle = errorCode.title;
                        this.errorConfig.messageSubtitle = errorCode?.subTitle;

                        this.notifyWidgetUpdated();

                        return;
                    }
                } catch {
                    // JSON parsing failed, fall through to generic error handling
                }
            }

            Sentry.errorTracker({
                message: "SegmentsMonthlyVisitsGraphWidget: Error",
                e: error,
                level: "error",
                params: {
                    extra: {
                        metric: this.metric,
                    },
                },
            });

            this.widgetState = WidgetState.ERROR;
        }
    }

    public getProUrl() {
        const { webSource, country, duration, key, comparedDuration, filters } = this._widgetConfig?.properties;

        const state = this._metricConfig.state;
        let id = key?.[0]?.id || key?.[0]?.segmentId;

        if (!id) {
            id = filters?.segmentId;
        }

        const result = {
            duration,
            country,
            webSource,
            id,
            mode: "single",
            comparedDuration,
        };

        if (typeof state === "undefined") {
            return this._swNavigator.href("home", {}, {});
        }

        return this._swNavigator.href(state, result, {});
    }
}

SegmentsMonthlyVisitsGraphWidget.register();
